import { useRef } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { Survey, DataTopicStatus } from "@/services/surveysListService"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  MapPin,
  FileText,
  Check,
  UsersRound,
  UserRound,
  CircleHelp,
  X,
  MessageSquareText,
  FileCheck,
  Pencil,
  ExternalLink,
} from "lucide-react"
import { Link } from "react-router-dom"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AddReportDialog from "@/components/survey-catalogue/AddReportDialog.tsx"
import { useSurveySort, SortableKey } from "@/hooks/useSurveySort"
import SortableColumnHeader from "@/components/survey-catalogue/SortableColumnHeader"
import TruncatedTextCell from "@/components/shared/TruncatedTextCell"
import {
  useInlineDataTopicEdit,
  DATA_TOPIC_OPTIONS,
} from "@/hooks/useInlineDataTopicEdit"
import UpdateReportDialog from "./UpdateReportDialog"

/**
 * Estimated height (in px) of a single rendered table row.
 *
 * Used as the virtualizer's initial guess before each row's real height is
 * measured. It need not be exact; a close value reduces first-paint shift.
 */
const ESTIMATED_ROW_HEIGHT_PX: number = 99

/**
 * Number of extra rows rendered above and below the visible viewport to keep
 * fast scrolling smooth by pre-rendering more rows just outside the visible
 * area, trading a little extra work for fewer blank gaps when scrolling quickly.
 */
const ROW_OVERSCAN: number = 25

export const columns: { key: keyof Survey; label: string }[] = [
  { key: "mapSurveyId", label: "MAP Survey ID" },
  { key: "countryName", label: "Country" },
  { key: "year", label: "Year" },
  { key: "surveyType", label: "Type" },
  { key: "populationRepresentativeness", label: "Targeted Population" },
  { key: "surveyStatus", label: "Status" },
  { key: "reportBiblioFile", label: "Report" },
  { key: "feverTreatmentSeeking", label: "Fever Treatment Seeking" },
  { key: "antimalarials", label: "Antimalarials" },
  { key: "chemoprevention", label: "Chemoprevention" },
  { key: "itn", label: "ITN" },
  { key: "irs", label: "IRS" },
  { key: "improvedHousing", label: "Improved Housing" },
  { key: "rdt", label: "RDT" },
  { key: "microscopy", label: "Microscopy" },
]

export const columnGroups: { keys: (keyof Survey)[]; label: string }[] = [
  { keys: ["mapSurveyId"], label: "MAP Survey ID" },
  { keys: ["countryName"], label: "Country" },
  { keys: ["year"], label: "Year" },
  { keys: ["surveyType"], label: "Type" },
  { keys: ["populationRepresentativeness"], label: "Targeted Population" },
  { keys: ["surveyStatus"], label: "Status" },
  { keys: ["reportBiblioFile"], label: "Report" },
  { keys: ["feverTreatmentSeeking"], label: "Fever Treatment Seeking" },
  { keys: ["antimalarials"], label: "Antimalarials" },
  { keys: ["chemoprevention"], label: "Chemoprevention" },
  { keys: ["itn"], label: "ITN" },
  { keys: ["irs"], label: "IRS" },
  { keys: ["improvedHousing"], label: "Improved Housing" },
  { keys: ["rdt"], label: "RDT" },
  { keys: ["microscopy"], label: "Microscopy" },
]

const sectionDividers = new Set<keyof Survey>(["reportBiblioFile", "hasGps", "feverTreatmentSeeking"])

const badgeColumns = new Set<keyof Survey>([
  "feverTreatmentSeeking",
  "antimalarials",
  "chemoprevention",
  "itn",
  "irs",
  "improvedHousing",
  "rdt",
  "microscopy",
])

//const codeButtonsDisabled: boolean = true

const TOPIC_STATUS_DISPLAY: Partial<
  Record<DataTopicStatus, { label: string; icon: React.ReactNode; bgbadge?: string }>
> = {
  [DataTopicStatus.MICRO_DATA_EXTRACTED]: {
    label: "Microdata extracted",
    icon: <UserRound className="text-green-600 mr-0.5" />,
    bgbadge: "bg-green-50 dark:bg-green-950",
  },
  [DataTopicStatus.DOES_CONTAIN]: {
    label: "Does contain",
    icon: <Check className="text-sky-800 mr-0.5" />,
    bgbadge: "bg-sky-50 dark:bg-sky-950",
  },
  [DataTopicStatus.MAY_CONTAIN]: {
    label: "May contain",
    icon: <CircleHelp className="text-muted-foreground mr-0.5" />,
    bgbadge: "bg-transparent",
  },
  [DataTopicStatus.DOES_NOT_CONTAIN]: {
    label: "Does not contain",
    icon: <X className="text-red-600 mr-0.5" />,
    bgbadge: "bg-red-50 dark:bg-red-950",
  },
  [DataTopicStatus.REPORT_DATA_EXTRACTED]: {
    label: "Report data extracted",
    icon: <FileCheck className="text-green-600 mr-0.5" />,
    bgbadge: "bg-green-50 dark:bg-green-950",
  },
}

function formatCellValue(value: Survey[keyof Survey]) {
  if (typeof value === "boolean") return value ? "Yes" : "No"
  return String(value)
}

function renderCell(
  key: keyof Survey,
  value: Survey[keyof Survey],
  survey: Survey,
  onAddReportFile: () => void,
  isEditing: boolean = false
) {
  if (key === "hasGps") return value ? <MapPin className="size-4 text-foreground" /> : null
  if (key === "hasMicro")
    return value ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <UsersRound className="size-4 text-foreground cursor-default" />
        </TooltipTrigger>
        <TooltipContent>Survey has microdata</TooltipContent>
      </Tooltip>
    ) : null
  if (key === "reportBiblioFile") {
    if (!value) return <AddReportDialog onReportCreated={onAddReportFile} survey={survey} />
    // In edit mode, swap the view link for the update dialog
    if (isEditing) return <UpdateReportDialog onReportCreated={onAddReportFile} survey={survey} />
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a href={String(value)} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="icon" className="size-7">
              <FileText />
            </Button>
          </a>
        </TooltipTrigger>
        <TooltipContent>Open survey report file in new tab</TooltipContent>
      </Tooltip>
    )
  }

  // Render undefined values as blank cells rather than the string "undefined".
  if (value === undefined || value === null) return null

  const text = formatCellValue(value)
  if (!badgeColumns.has(key)) return text

  const display = TOPIC_STATUS_DISPLAY[text as DataTopicStatus]
  return (
    <Badge
      variant="secondary"
      className={`[&>svg]:size-3.5! ${display?.bgbadge}`}
    >
      {display?.icon}
      {display?.label ?? text}
    </Badge>
  )
}

// const extractedValues = new Set<string>([
//   DataTopicStatus.MICRO_DATA_EXTRACTED,
//   DataTopicStatus.REPORT_DATA_EXTRACTED,
// ])

// function hasExtractedData(survey: Survey): boolean {
//   return [...badgeColumns].some((key) => extractedValues.has(String(survey[key])))
// }

interface SurveyListTableProps {
  surveys: Survey[]
  visibleKeys: Set<keyof Survey>
  /** Callback invoked after a survey's data topics are updated. */
  onSurveyUpdated: () => Promise<void>
  /** Callback invoked when the user clicks the notes button for a survey. */
  onViewNotes: (survey: Survey) => void
  /** Callback invoked when the user clicks the button to add report to a survey. */
  onAddReportFile: () => void
  /** Mapping of survey type codes to their human-readable descriptions. */
  surveyTypeLabels: Record<string, string>
}

/** Set of column keys that support sorting via click-to-sort headers. */
const sortableColumns = new Set<keyof Survey>(["year", "surveyType"])

/**
 * Total horizontal padding (px) applied to a table cell by the `p-2` class
 * (0.5rem left + 0.5rem right = 16px). Used to compute the exact pixel width
 * available to a sticky cell's inner content so it truncates at the right point.
 */
const CELL_HORIZONTAL_PADDING_PX: number = 16

const SurveyExpandedTable = ({
  surveys,
  visibleKeys,
  onSurveyUpdated,
  onViewNotes,
  onAddReportFile,
  surveyTypeLabels,
}: SurveyListTableProps) => {
  const { sortedSurveys, sortConfig, toggleSort, getRowKey } = useSurveySort(surveys)
  const {
    editingRowKey,
    formValues,
    submitting,
    submitError,
    openEdit,
    closeEdit,
    handleFieldChange,
    handleSave,
  } = useInlineDataTopicEdit(onSurveyUpdated)
  const visibleColumns = columns.filter(c => visibleKeys.has(c.key))

  const isMapSurveyIdVisible = visibleKeys.has("mapSurveyId")
  const isCountryNameVisible = visibleKeys.has("countryName")

  const mapSurveyIdWidth = 160 // px
  const countryNameWidth = 160 // px

  // Total number of columns rendered per row, including the trailing actions
  // column. Used as the colSpan for the empty-state and padding rows.
  const totalColumnCount: number = visibleColumns.length + 1

  // The scrollable element that wraps the table; the virtualizer measures and
  // listens to it to decide which rows fall inside the viewport.
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Virtualizer: renders only the rows currently visible (plus overscan)
  // instead of all `sortedSurveys`, keeping the DOM small even for thousands
  // of surveys. Row heights are measured dynamically.
  const rowVirtualizer = useVirtualizer({
    count: sortedSurveys.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
    overscan: ROW_OVERSCAN,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const totalSize: number = rowVirtualizer.getTotalSize()

  // Spacer heights that stand in for the rows not currently rendered, so the
  // scrollbar length and scroll position stay correct.
  const paddingTop: number = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom: number =
    virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0

  return (
    <div className="relative flex-1 min-h-0">
      {/* Saving overlay — covers the table while the API call is in flight. */}
      {submitting && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/60 backdrop-blur-sm">
          <LoadingSpinner loadingContent="Saving changes..." variant="outline" size="sm" />
        </div>
      )}
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 h-full overflow-auto border border-border/50 [&>[data-slot=table-container]]:overflow-x-visible"
      >
      <Table className="relative border-separate border-spacing-0 min-w-max">
        <TableHeader className="sticky top-0 z-30 bg-card shadow-sm">
          <TableRow>
            {visibleColumns.map(col => {
              if (col.key === "hasMicro") return null
              const colSpan =
                col.key === "reportBiblioFile" && visibleKeys.has("hasMicro") ? 2 : undefined

              const isSticky = col.key === "mapSurveyId" || col.key === "countryName"
              let leftStyle: React.CSSProperties = {}
              if (col.key === "mapSurveyId") {
                leftStyle = {
                  left: 0,
                  width: `${mapSurveyIdWidth}px`,
                  minWidth: `${mapSurveyIdWidth}px`,
                  maxWidth: `${mapSurveyIdWidth}px`,
                }
              } else if (col.key === "countryName") {
                leftStyle = {
                  left: isMapSurveyIdVisible ? `${mapSurveyIdWidth}px` : 0,
                  width: `${countryNameWidth}px`,
                  minWidth: `${countryNameWidth}px`,
                  maxWidth: `${countryNameWidth}px`,
                }
              }

              const isRightmostSticky =
                col.key === "countryName" || (col.key === "mapSurveyId" && !isCountryNameVisible)

              return (
                <TableHead
                  key={col.key}
                  colSpan={colSpan}
                  style={isSticky ? leftStyle : undefined}
                  className={
                    [
                      "cursor-default",
                      sectionDividers.has(col.key) ? "border-l" : "",
                      isSticky ? "md:sticky md:z-30 bg-card" : "",
                      isRightmostSticky
                        ? "md:border-r"
                        : "",
                      col.key === "surveyType" ? "max-w-16" : "",
                    ]
                      .join(" ")
                      .trim() || undefined
                  }
                >
                  {sortableColumns.has(col.key) ? (
                    <SortableColumnHeader
                      columnKey={col.key as SortableKey}
                      label={col.label}
                      sortConfig={sortConfig}
                      onToggleSort={toggleSort}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              )
            })}
            <TableHead className="bg-card md:sticky right-0 md:z-30 border-l " />
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedSurveys.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={totalColumnCount}
                className="text-center text-muted-foreground py-8"
              >
                No surveys found.
              </TableCell>
            </TableRow>
          ) : (
            <>
              {/* Top spacer: occupies the height of the rows scrolled past. */}
              {paddingTop > 0 && (
                <tr aria-hidden="true">
                  <td colSpan={totalColumnCount} style={{ height: `${paddingTop}px` }} />
                </tr>
              )}
              {virtualRows.map(virtualRow => {
                const survey = sortedSurveys[virtualRow.index]
                const rowKey = getRowKey(survey)
                const isEditing = editingRowKey === rowKey
                const isDimmed = editingRowKey !== null && !isEditing
                return (
                  <TableRow
                    key={rowKey}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    className={[
                      "transition-colors",
                      isDimmed ? "pointer-events-none" : "hover:bg-accent/30",
                      isEditing ? "bg-accent/20" : "",
                    ].join(" ")}
                  >
                    {visibleColumns.map(col => {
                      const isSticky = col.key === "mapSurveyId" || col.key === "countryName"
                      let leftStyle: React.CSSProperties = {}
                      if (col.key === "mapSurveyId") {
                        leftStyle = {
                          left: 0,
                          width: `${mapSurveyIdWidth}px`,
                          minWidth: `${mapSurveyIdWidth}px`,
                          maxWidth: `${mapSurveyIdWidth}px`,
                        }
                      } else if (col.key === "countryName") {
                        leftStyle = {
                          left: isMapSurveyIdVisible ? `${mapSurveyIdWidth}px` : 0,
                          width: `${countryNameWidth}px`,
                          minWidth: `${countryNameWidth}px`,
                          maxWidth: `${countryNameWidth}px`,
                        }
                      }

                      const isRightmostSticky =
                        col.key === "countryName" ||
                        (col.key === "mapSurveyId" && !isCountryNameVisible)

                      return (
                        <TableCell
                          key={col.key}
                          style={isSticky ? leftStyle : undefined}
                          className={
                            [
                              "cursor-default",
                              sectionDividers.has(col.key) ? "border-l" : "",
                              isSticky ? "md:sticky md:z-10 bg-card border-b" : "border-b",
                              isDimmed && !isSticky ? "opacity-30" : "",
                              isRightmostSticky ? "md:border-r border-b" : "",
                              col.key === "surveyType" ? "max-w-16" : "",
                              col.key === "reportBiblioFile" ? "text-center" : "",
                              col.key === "surveyStatus" ? "min-w-[180px]" : "",
                            ]
                              .join(" ")
                              .trim() || undefined
                          }
                        >
                          {col.key === "surveyType" ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="block truncate cursor-default">
                                  {renderCell(col.key, survey[col.key], survey, onAddReportFile)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {surveyTypeLabels[String(survey[col.key])] ??
                                  String(survey[col.key])}
                              </TooltipContent>
                            </Tooltip>
                          ) : col.key === "mapSurveyId" || col.key === "countryName" ? (
                            <TruncatedTextCell
                              text={survey[col.key] != null ? String(survey[col.key]) : ""}
                              widthPx={
                                (col.key === "mapSurveyId" ? mapSurveyIdWidth : countryNameWidth) -
                                CELL_HORIZONTAL_PADDING_PX
                              }
                            />
                          ) : isEditing && badgeColumns.has(col.key) ? (
                            // Edit mode: replace the badge with an inline dropdown.
                            <Select
                              value={
                                formValues[col.key as keyof typeof formValues] ??
                                String(survey[col.key])
                              }
                              onValueChange={value =>
                                handleFieldChange(col.key as string, value as DataTopicStatus)
                              }
                            >
                              <SelectTrigger className="h-7 text-xs w-full min-w-[130px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {DATA_TOPIC_OPTIONS.map(option => (
                                  <SelectItem key={option} value={option} className="text-xs">
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            renderCell(col.key, survey[col.key], survey, onAddReportFile, isEditing)
                          )}
                        </TableCell>
                      )
                    })}
                    <TableCell className="px-3 w-0 bg-card md:sticky right-0 md:z-20 border-l md:shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.15)]">
                      <div
                        className={`flex flex-col items-start gap-1 ${isDimmed ? "opacity-30" : ""}`}
                      >
                        <div className="flex items-center gap-1">
                          {isEditing ? (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="default"
                                    size="icon"
                                    className="size-7 bg-green-500"
                                    onClick={() => handleSave(survey)}
                                    disabled={submitting}
                                  >
                                    <Check className="size-3.5" />
                                    <span className="sr-only">Save data topics</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>{submitting ? "Saving…" : "Save"}</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="destructive"
                                    size="icon"
                                    className="size-7"
                                    onClick={closeEdit}
                                    disabled={submitting}
                                  >
                                    <X className="size-3.5" />
                                    <span className="sr-only">Cancel</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Cancel</TooltipContent>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="size-7"
                                    onClick={() => openEdit(survey, rowKey)}
                                  >
                                    <Pencil className="size-3.5" />
                                    <span className="sr-only">Edit report and/or data topics</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Edit report and/or data topics</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="relative size-7"
                                    onClick={() => onViewNotes(survey)}
                                  >
                                    <MessageSquareText className="size-3.5" />
                                    <span className="sr-only">View notes</span>
                                    {(survey.notes?.length ?? 0) > 0 && (
                                      <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary text-primary-foreground px-1 text-[10px] font-medium leading-none">
                                        {survey.notes?.length}
                                      </span>
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>View notes</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="size-7"
                                    asChild
                                  >
                                    <Link to={`/survey-catalogue/${survey.mapSurveyId}`} state={{ survey }}>
                                      <ExternalLink className="size-3.5" />
                                      <span className="sr-only">Open survey</span>
                                    </Link>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Open survey</TooltipContent>
                              </Tooltip>
                              {/*
                              {hasExtractedData(survey) && (
                                <SurveyCodeSnippets
                                  survey={survey}
                                  disabled={codeButtonsDisabled}
                                />
                              )}
                              */}
                            </>
                          )}
                        </div>
                        {isEditing && submitError && (
                          <p
                            className="text-[10px] text-destructive max-w-[120px] text-right"
                            role="alert"
                          >
                            {submitError}
                          </p>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
              {/* Bottom spacer: occupies the height of the rows not yet reached. */}
              {paddingBottom > 0 && (
                <tr aria-hidden="true">
                  <td colSpan={totalColumnCount} style={{ height: `${paddingBottom}px` }} />
                </tr>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </div>
    </div>
  )
}

export default SurveyExpandedTable
